// Add-to-cart for the merchant-configured add-on rows inside the cart drawer.
//
// Delegated from <cart-drawer> rather than bound per button, because the drawer
// replaces its own innerHTML on every cart update - per-button listeners would
// be discarded on the first render.
if (!window.__cartDrawerAddonsBound) {
  window.__cartDrawerAddonsBound = true;

  // Shopify's /cart/add.js takes roughly a second to answer, and nothing we do
  // in the theme makes that faster - measured, the section rendering we ask for
  // in the same call adds no meaningful time. So instead of waiting for it, show
  // the line immediately and reconcile when the response lands.
  //
  // The row is CLONED from a real cart line rather than hand-built: that keeps it
  // byte-identical to the drawer's own markup (grid areas, ARIA roles, classes)
  // with no second copy of the template to maintain. It only has to look right
  // for ~1s, because renderContents() replaces the whole drawer on response.
  function insertOptimisticLine(tile) {
    const drawer = document.querySelector('#CartDrawer');
    const template = drawer && drawer.querySelector('.cart-item');
    if (!template) return null;

    const list = template.parentElement;
    const row = template.cloneNode(true);
    row.classList.add('cart-item--pending');
    row.removeAttribute('id');

    const image = row.querySelector('.cart-item__image');
    const tileImage = tile && tile.querySelector('.cart-drawer-addons__media img');
    if (image && tileImage) {
      image.setAttribute('src', tileImage.currentSrc || tileImage.src);
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.removeAttribute('loading');
    } else if (image) {
      image.remove();
    }

    const name = row.querySelector('.cart-item__name');
    const tileTitle = tile && tile.querySelector('.cart-drawer-addons__title');
    if (name && tileTitle) {
      name.textContent = tileTitle.textContent.trim();
      name.removeAttribute('href');
    }

    // The variant line and any discount/error text belong to the cloned product,
    // not this one. Drop them rather than show something wrong for a second.
    row.querySelectorAll('.product-option, dl, .cart-item__discounted-prices, .cart-item__error, .cart-item__saving, .cart-item__old-price, s').forEach((el) => el.remove());

    const priceWrapper = row.querySelector('.cart-item__price-wrapper');
    const tilePrice = tile && tile.querySelector('.cart-drawer-addons__price span');
    if (priceWrapper && tilePrice) {
      priceWrapper.textContent = tilePrice.textContent.trim();
    }

    const quantity = row.querySelector('.quantity__input');
    if (quantity) {
      quantity.value = '1';
      quantity.setAttribute('value', '1');
    }

    // Nothing here is wired to a real cart line, so make sure it cannot be
    // clicked, typed into, or tabbed to during the second it exists.
    row.querySelectorAll('a, button, input, select, cart-remove-button').forEach((el) => {
      el.setAttribute('tabindex', '-1');
      el.setAttribute('aria-hidden', 'true');
      if ('disabled' in el) el.disabled = true;
    });

    list.appendChild(row);
    row.scrollIntoView({ block: 'nearest' });
    return row;
  }

  function showError(message) {
    const target = document.getElementById('CartDrawer-CartErrors');
    if (!target || !message) return;
    target.textContent = message;
    target.classList.add('cart-drawer-addons__error');
    setTimeout(() => {
      if (target.textContent === message) {
        target.textContent = '';
        target.classList.remove('cart-drawer-addons__error');
      }
    }, 6000);
  }

  // Closes the drawer from the continue-shopping link under the checkout button.
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-cart-continue-shopping]')) return;
    event.preventDefault();
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-addon-add]');
    if (!button) return;

    event.preventDefault();
    if (button.getAttribute('aria-disabled') === 'true') return;

    const variantId = Number.parseInt(button.dataset.cartAddonAdd, 10);
    if (!Number.isFinite(variantId) || variantId <= 0) return;

    const drawer = document.querySelector('cart-drawer');

    // Optimistic UI. The server round trip is unavoidable, but the shopper
    // should never be looking at a button that appears to have ignored them.
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    const spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.remove('hidden');

    const tile = button.closest('.cart-drawer-addons__item');
    if (tile) tile.classList.add('is-added');

    const pendingRow = insertOptimisticLine(tile);

    // Bump every visible count straight away, otherwise the header reads
    // "Basket - 1" while two lines are on screen. The section render reconciles
    // them all a second later.
    const counters = [
      document.querySelector('.cart-count-bubble span[aria-hidden="true"]'),
      document.querySelector('#CartDrawer .drawer__heading-count'),
    ].filter(Boolean);

    const previousCounts = counters.map((el) => el.textContent);
    counters.forEach((el) => {
      const parsed = Number.parseInt(el.textContent.trim(), 10);
      if (Number.isFinite(parsed)) el.textContent = String(parsed + 1);
    });

    const revertOptimistic = () => {
      if (pendingRow && pendingRow.isConnected) pendingRow.remove();
      if (tile) tile.classList.remove('is-added');
      counters.forEach((el, i) => {
        el.textContent = previousCounts[i];
      });
    };

    try {
      const body = { items: [{ id: variantId, quantity: 1 }] };

      // Ask for the same sections the drawer re-renders itself with, so the
      // line items, totals and free shipping bar all refresh in one round trip.
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      const response = await fetch(`${routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();

      if (result.status) {
        // The line was already shown optimistically, so a silent rollback would
        // look like the click did nothing. Say why it went away.
        console.error('Cart drawer add-on failed:', result.description || result.message);
        revertOptimistic();
        showError(result.description || result.message);
        return;
      }

      if (drawer && typeof drawer.renderContents === 'function') {
        // renderContents expects a single-item shape plus sections.
        drawer.renderContents({
          ...(Array.isArray(result.items) ? result.items[0] : result),
          sections: result.sections,
        });
      } else {
        window.location.reload();
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-addons', productVariantId: variantId });
      }
    } catch (error) {
      console.error('Cart drawer add-on request failed:', error);
      revertOptimistic();
      showError((window.cartStrings && window.cartStrings.error) || 'Sorry, that could not be added.');
    } finally {
      // The drawer usually re-renders and replaces this button, so these only
      // matter on the error paths - but they must not be skipped there.
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }
  });
}
